import { useAuth } from '../../context/AuthContext';
import React, { useEffect, useState } from 'react';
import { FaDatabase, FaGlobeAmericas, FaTable } from 'react-icons/fa';
import Btn from '../../components/ui/Btn';
import Modal from '../../components/ui/Modal';
import Field, { TextInput, TextArea, NativeSelect } from '../../components/ui/Field';
import { toolsService } from '../../services/toolsService';
import { friendlyError } from '../../services/agentsService';

export const KINDS = {
  supabase: { label: 'Supabase', icon: FaDatabase, lead: 'Una base Supabase con su URL y su service role key. Sirve para guardar, buscar o actualizar datos.' },
  http: { label: 'API por HTTPS', icon: FaGlobeAmericas, lead: 'Cualquier servicio con una dirección https (un CRM, un webhook de n8n, una API propia).' },
  gsheets: { label: 'Google Sheets', icon: FaTable, lead: 'Hojas de cálculo de Google con una cuenta de servicio. Sirve para agregar, buscar o actualizar filas.' },
};
const AUTHS = [
  { key: 'bearer', label: 'Token (Authorization: Bearer)' },
  { key: 'header', label: 'Clave en un encabezado propio' },
  { key: 'basic', label: 'Usuario y contraseña (Basic)' },
  { key: 'none', label: 'Sin autenticación' },
];

/**
 * Alta o edición de un conector. Se usa en Integraciones y dentro del editor de herramientas (crear el conector sin salir).
 * `lockKind`: el tipo ya está decidido (no se muestra la elección). `defaultScope`: 'global' o el id del proyecto.
 * `onSaved(mensaje, resultado)`: resultado = { id, public_id, last4 } de connector_save.
 */
export default function ConnectorModal({ open, connector, projects, onClose, onSaved, lockKind = null, defaultScope = 'global' }) {
  const { currentUser } = useAuth() || {};
  // un empleado solo crea conectores de su proyecto interno (nunca globales ni de clientes)
  const lockedProject = currentUser?.role === 'employee' ? currentUser.employeeProject : null;
  const isNew = !connector?.id;
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    const c = connector || {};
    setF({ kind: c.kind || lockKind || 'supabase', name: c.name || '', scope: c.id ? (c.project_id === null ? 'global' : String(c.project_id)) : String(lockedProject ?? defaultScope),
      url: c.config?.url || '', base_url: c.config?.base_url || '', auth: c.config?.auth || 'bearer', header_name: c.config?.header_name || 'X-Api-Key', secret: '' });
    setError('');
  }, [open, connector, lockKind, defaultScope, lockedProject]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const needsSecret = f.kind !== 'http' || f.auth !== 'none';

  const save = async () => {
    setBusy(true); setError('');
    try {
      const config = f.kind === 'gsheets' ? {} : f.kind === 'supabase' ? { url: f.url.trim() } : { base_url: f.base_url.trim(), auth: f.auth, ...(f.auth === 'header' ? { header_name: f.header_name.trim() } : {}) };
      const r = await toolsService.saveConnector({ id: connector?.id ?? null, projectId: f.scope === 'global' ? null : Number(f.scope), kind: f.kind, name: f.name.trim(), config, secret: f.secret });
      onSaved(isNew ? 'Conector agregado' : 'Conector guardado', { ...r, name: f.name.trim() });
    } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={isNew ? (lockKind ? `Nuevo conector de ${KINDS[lockKind].label}` : 'Agregar conector') : `Editar «${connector?.name}»`} width="max-w-xl"
      description="La clave se guarda cifrada y no se vuelve a mostrar: aquí solo se verán sus últimos 4 caracteres."
      footer={<><Btn variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Btn><Btn variant="primary" onClick={save} loading={busy} disabled={!f.name?.trim()}>{isNew ? 'Agregar conector' : 'Guardar'}</Btn></>}>
      <div className="space-y-4">
        {isNew && !lockKind && (
          <fieldset>
            <legend className="text-[13px] font-medium text-fg mb-2">Tipo</legend>
            <div className="grid sm:grid-cols-3 gap-2">
              {Object.entries(KINDS).map(([k, K]) => (
                <label key={k} className={`relative flex gap-3 cursor-pointer rounded-xl p-3 ring-1 ${f.kind === k ? 'ring-fg bg-raised' : 'ring-line hover:ring-fg/20'}`}>
                  <input type="radio" name="kind" value={k} checked={f.kind === k} onChange={set('kind')} className="sr-only" />
                  <K.icon aria-hidden className="mt-0.5 text-fg-dim shrink-0" />
                  <span><span className="block text-[14px] font-semibold text-fg">{K.label}</span><span className="block text-[12.5px] text-fg-dim">{K.lead}</span></span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <Field label="Nombre" hint="Para reconocerlo, por ejemplo «Supabase de Clínica Sol».">
          <TextInput value={f.name || ''} onChange={set('name')} maxLength={80} />
        </Field>
        <Field label="Quién lo puede usar" hint={isNew ? 'Global sirve para herramientas de la biblioteca que usan muchos agentes con el mismo destino.' : 'No se puede cambiar después de crearlo.'}>
          <NativeSelect value={f.scope || 'global'} onChange={set('scope')} disabled={!isNew || Boolean(lockedProject)}>
            {!lockedProject && <option value="global">Global: cualquier proyecto</option>}
            {projects.map((p) => <option key={p.id} value={String(p.id)}>Solo el proyecto «{p.name}»</option>)}
          </NativeSelect>
        </Field>
        {f.kind === 'gsheets' ? (
          <>
            {connector?.config?.client_email && <p className="text-[13px] text-fg-dim">Cuenta actual: <span className="font-mono text-fg">{connector.config.client_email}</span></p>}
            <Field label="JSON de la cuenta de servicio" hint={isNew
              ? 'En Google Cloud: IAM → Cuentas de servicio → Claves → Agregar clave (JSON), con la API de Google Sheets activada en ese proyecto. Pega el archivo completo. Después comparte cada hoja con el correo de la cuenta.'
              : 'Déjalo vacío para conservar la cuenta actual.'}>
              <TextArea rows={5} value={f.secret || ''} onChange={set('secret')} spellCheck={false} autoComplete="off" className="font-mono text-[12px]" placeholder='{"type": "service_account", "client_email": "…", "private_key": "…"}' />
            </Field>
          </>
        ) : f.kind === 'supabase' ? (
          <>
            <Field label="URL del proyecto de Supabase" hint="En Supabase: Project Settings → API → Project URL. Sin /rest/v1.">
              <TextInput value={f.url || ''} onChange={set('url')} placeholder="https://xxxx.supabase.co" spellCheck={false} inputMode="url" />
            </Field>
            <Field label="Service role key" hint={isNew ? 'Project Settings → API → service_role. Salta las reglas de seguridad (RLS) de esa base: cada herramienta solo toca las tablas que le indiques.' : 'Déjalo vacío para conservar la clave actual.'}>
              <TextInput type="password" value={f.secret || ''} onChange={set('secret')} autoComplete="off" spellCheck={false} placeholder={connector?.last4 ? `…${connector.last4}` : ''} />
            </Field>
          </>
        ) : (
          <>
            <Field label="URL base" hint="Las herramientas agregan la ruta: por ejemplo https://api.cliente.mx/v1 + /leads.">
              <TextInput value={f.base_url || ''} onChange={set('base_url')} placeholder="https://api.ejemplo.mx/v1" spellCheck={false} inputMode="url" />
            </Field>
            <Field label="Autenticación">
              <NativeSelect value={f.auth} onChange={set('auth')}>{AUTHS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}</NativeSelect>
            </Field>
            {f.auth === 'header' && <Field label="Nombre del encabezado"><TextInput value={f.header_name || ''} onChange={set('header_name')} spellCheck={false} /></Field>}
            {needsSecret && (
              <Field label={f.auth === 'basic' ? 'Usuario y contraseña (usuario:contraseña)' : 'Clave o token'} hint={!isNew ? 'Déjalo vacío para conservar la clave actual.' : undefined}>
                <TextInput type="password" value={f.secret || ''} onChange={set('secret')} autoComplete="off" spellCheck={false} placeholder={connector?.last4 ? `…${connector.last4}` : ''} />
              </Field>
            )}
          </>
        )}
        {error && <p role="alert" className="text-[13px] text-signal">{error}</p>}
      </div>
    </Modal>
  );
}
